import {useMemo} from 'react'
import {
  type PluginHook,
  useBlockLayout,
  useColumnOrder,
  useResizeColumns,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'

import {useFilteredItems} from '../../../features/filtering/hooks/use-filtered-items'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useSortedItems} from '../../../features/sorting/hooks/use-sorted-items'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useSortedBy} from '../../../hooks/use-sorted-by'
import type {ColumnModel} from '../../../models/column-model'
import {useMemexItems} from '../../../state-providers/memex-items/use-memex-items'
import {useSearch} from '../../filter-bar/search-context'
import {RowDragHandleColumnId} from '../column-ids'
import {getRowId} from '../get-row-id'
import {orderByFn} from '../sort/order-by-fn'
import {useTableColumns} from '../state-providers/table-columns/use-table-columns'
import type {TableDataType} from '../table-data-type'
import {useColumnReordering} from '../use-column-reordering'
import {useCustomGlobalFilter} from '../use-custom-global-filter'
import {useCustomGroupBy} from '../use-custom-group-by'
import {useDeselectAllRows} from '../use-deselect-all-rows'
import {usePersistColumnResize} from '../use-persist-column-resize'
import {usePreventColumnAutoResize} from '../use-prevent-column-auto-resize'
import {useRanking} from '../use-ranking'
import {useRowDragHandle} from '../use-row-drag-handle'
import {useRowMenuShortcut} from '../use-row-menu-shortcut'
import {useSortedByGroupOrdering} from '../use-sorted-by-group-ordering'

type TableProps = {
  fields: ReadonlyArray<ColumnModel>
  plugins: Array<PluginHook<TableDataType>>
}

export function useReactTable({fields, plugins}: TableProps) {
  const {sorts} = useSortedBy()
  const {groupedByColumnId, collapsedGroups} = useHorizontalGroupedBy()

  const {columnDefinitions, hiddenColumns} = useTableColumns()

  const sortBy = useMemo(
    () => sorts.map(sort => ({id: sort.column.id.toString(), desc: sort.direction === 'desc'})),
    [sorts],
  )

  const groupBy = useMemo(() => (groupedByColumnId ? [`${groupedByColumnId}`] : []), [groupedByColumnId])

  /**
   * Must apply this here instead of in the useRowDragDrop hook to ensure it applies on the final set of fields
   * and not before we override the useControlledState hook.
   */
  const columnOrder = useMemo(() => {
    return [RowDragHandleColumnId, ...fields.map(field => field.id.toString())]
  }, [fields])

  const {memex_table_without_limits, memex_prefilter_table_items} = useEnabledFeatures()
  const optionalFilterPlugin = memex_table_without_limits ? [] : [useCustomGlobalFilter]

  const tablePlugins: Array<PluginHook<TableDataType>> = [
    useBlockLayout,
    useColumnOrder,
    useSortBy,
    useCustomGroupBy,
    ...optionalFilterPlugin,
    useSortedByGroupOrdering,
    useRanking,
    useColumnReordering,
    useRowMenuShortcut,
    useRowDragHandle,
    usePreventColumnAutoResize,
    useResizeColumns,
    usePersistColumnResize,
    useRowSelect,
    useDeselectAllRows,
    ...plugins,
  ]

  const {query, globalFilter} = useSearch()

  // Sorting and filtering is applied server-side for memex_table_without_limits
  // so there's no need to sort and filter again
  const disableSortAndFilterPlugins = memex_prefilter_table_items || memex_table_without_limits

  const data = useReactTableData()

  const table = useTable<TableDataType>(
    {
      columns: columnDefinitions,
      data,
      ...(disableSortAndFilterPlugins ? {} : {globalFilter}),
      orderByFn,
      getRowId,
      initialState: {
        hiddenColumns,
        sortBy,
        groupByColumnIds: groupBy,
        collapsedGroups,
        ...(disableSortAndFilterPlugins ? {} : {globalFilter: query}),
        columnOrder,
      },
      ...(disableSortAndFilterPlugins ? {manualSortBy: true} : {autoResetSortBy: false, disableMultiSort: true}),
      autoResetHiddenColumns: false,
      autoResetSelectedRows: false,
      autoResetResize: true,
      /**
       * These allow us to control react-table's internal state based
       * on outer scope state, without having to manually
       * call table[setXXXX|dispatch]. This also occurs in the
       * initial rendering phase instead of causing an effect to reset
       * state slices
       *
       * This must be memoized deeply, to avoid excessively
       * causing object.is checks to fail, and requires merging the
       * current state with the overrides
       */
      useControlledState(state) {
        return useMemo(() => {
          return {
            ...state,
            sortBy,
            ...(disableSortAndFilterPlugins ? {} : {globalFilter: query}),
            groupByColumnIds: groupBy,
            collapsedGroups,
            hiddenColumns,
            columnOrder,
          }
          /**
           * because of how this is scoped inside another 'hook',
           * react-hooks-eslint plugin has a hard time understanding its
           * dependencies.
           */
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state, columnOrder, sortBy, query, groupBy, hiddenColumns, collapsedGroups])
      },
    },
    ...tablePlugins,
  )

  return table
}

function useReactTableData() {
  const {memex_table_without_limits} = useEnabledFeatures()
  if (memex_table_without_limits) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useReactTableDataForPaginatedData()
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useReactTableDataForUnpaginatedData()
  }
}

function useReactTableDataForPaginatedData() {
  const {items} = useMemexItems()

  // Soon this is where we will prepare the data for grouping if applicable
  return useMemo(() => items, [items])
}

function useReactTableDataForUnpaginatedData() {
  const {items} = useMemexItems()
  const {memex_prefilter_table_items} = useEnabledFeatures()

  // Items are received from the server in priority order.
  // The raw priority value exceeds JavaScript MAX_SAFE_INTEGER,
  // so we map the item's index which represents its relative
  // priority within the current set of items.
  let itemsForTable: ReadonlyArray<TableDataType> = useMemo(
    () =>
      items.map((item, index) => {
        if (memex_prefilter_table_items) {
          item.prioritizedIndex = index
        }
        return item
      }),
    [items, memex_prefilter_table_items],
  )

  if (memex_prefilter_table_items) {
    // We want to skip this dependency when the FF is disabled:
    // Feature flags are stable for the lifetime
    // of the app, so it is safe to disable this rule
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const {filteredItems} = useFilteredItems()
    itemsForTable = filteredItems
  }
  const {sortUngroupedItems} = useSortedItems()
  return useMemo(
    () => (memex_prefilter_table_items ? sortUngroupedItems(itemsForTable) : itemsForTable),
    [itemsForTable, memex_prefilter_table_items, sortUngroupedItems],
  )
}
